import React, { useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Flex,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  SimpleGrid,
  Icon,
  Select,
} from '@chakra-ui/react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  LabelList,
} from 'recharts';
import {
  CurrencyDollar,
  Receipt,
  TrendUp,
  ChartPie,
  Users,
  Percent,
  Wallet,
  Vault,
  Calculator,
  Buildings,
  UserPlus,
  UserMinus,
  Sun,
  FirstAidKit,
} from 'phosphor-react';
import { IDossieData } from '../utils/dossieTypes';

interface DossieDashboardProps {
  data: IDossieData;
  isLoading: boolean;
  anoFolha?: number;
  // eslint-disable-next-line no-unused-vars
  onAnoFolhaChange?: (ano: number) => void;
  isLoadingFolha?: boolean;
}

interface PieDataItem {
  name: string;
  value: number;
}

const PIE_COLORS = ['#3182CE', '#38A169', '#DD6B20', '#805AD5', '#D53F8C', '#E53E3E'];

const mesesNomes = [
  '',
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

export function DossieDashboard({
  data,
  isLoading,
  anoFolha,
  onAnoFolhaChange,
  isLoadingFolha = false,
}: DossieDashboardProps) {
  const { faturamento, folha } = data;

  // Formatar moeda
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value || 0);
  };

  // Formatar moeda compacta
  const formatCurrencyCompact = (value: number) => {
    if (value >= 1000000) {
      return `R$ ${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `R$ ${(value / 1000).toFixed(1)}K`;
    }
    return formatCurrency(value);
  };

  // Calcular totais do faturamento
  const totais = useMemo(() => {
    return faturamento.reduce(
      (acc, item) => ({
        faturamento: acc.faturamento + (item.total_faturamento || 0),
        saidas: acc.saidas + (item.total_saidas || 0),
        servicos: acc.servicos + (item.total_servicos || 0),
        pis: acc.pis + (item.pis || 0),
        cofins: acc.cofins + (item.cofins || 0),
        icms: acc.icms + (item.icms || 0),
        sn: acc.sn + (item.sn || 0),
        csll: acc.csll + (item.csll || 0),
        irpj: acc.irpj + (item.irpj || 0),
      }),
      {
        faturamento: 0,
        saidas: 0,
        servicos: 0,
        pis: 0,
        cofins: 0,
        icms: 0,
        sn: 0,
        csll: 0,
        irpj: 0,
      },
    );
  }, [faturamento]);

  // Total de impostos fiscais
  const totalImpostosFiscal =
    totais.pis + totais.cofins + totais.icms + totais.sn + totais.csll + totais.irpj;

  // Totais da folha
  const totaisFolha = useMemo(() => {
    if (!folha) {
      return {
        admissoes: 0,
        demissoes: 0,
        ferias: 0,
        afastamentos: 0,
        fgts: 0,
        dctfweb: 0,
        vidas: 0,
        prolabore: 0,
      };
    }
    return {
      admissoes: folha.totais?.admissoes || 0,
      demissoes: folha.totais?.demissoes || 0,
      ferias: folha.totais?.ferias || 0,
      afastamentos: folha.totais?.afastamentos || 0,
      fgts: folha.totais?.fgts || 0,
      dctfweb: folha.totais?.dctfweb || 0,
      vidas: folha.qnt_vidas || 0,
      prolabore: folha.qnt_prolabore || 0,
    };
  }, [folha]);

  // Total de impostos DP (FGTS + DCTF-Web)
  const totalImpostosDP = totaisFolha.fgts + totaisFolha.dctfweb;

  // Total geral (Fiscal + DP)
  const totalGeralImpostos = totalImpostosFiscal + totalImpostosDP;

  // Porcentagem de impostos sobre faturamento
  const percentualImpostos =
    totais.faturamento > 0 ? (totalImpostosFiscal / totais.faturamento) * 100 : 0;

  // Detecta se é Simples Nacional baseado nos impostos existentes
  const isSimples = useMemo(() => {
    // Soma dos impostos federais tradicionais (Lucro Presumido/Real)
    const impostosFederais = totais.pis + totais.cofins + totais.csll + totais.irpj;

    // Se tem SN > 0 e impostos federais são zero → Simples Nacional
    if (totais.sn > 0 && impostosFederais === 0) return true;

    // Se tem impostos federais > 0 e SN é zero → Lucro Presumido/Real
    if (impostosFederais > 0 && totais.sn === 0) return false;

    // Se ambos têm valores (empresa pode ter mudado de regime) → comparar qual é maior
    if (totais.sn > 0 && impostosFederais > 0) {
      return totais.sn > impostosFederais;
    }

    // Se ambos são zero → não há dados suficientes
    return false;
  }, [totais]);

  // Flag para saber se há dados de impostos para exibir
  const hasImpostosData =
    totais.sn > 0 || totais.pis > 0 || totais.cofins > 0 || totais.csll > 0 || totais.irpj > 0;

  // Cálculos de projeção mensal
  const projecaoMensal = useMemo(() => {
    const numMeses = faturamento.length || 1;

    if (isSimples) {
      const mediaMensal = totais.sn / numMeses;
      return {
        mediaMensal,
        impostosMensais: 0,
        reservaTrimestrais: 0,
        totalEsperado: mediaMensal,
      };
    }

    const totalMensais = totais.pis + totais.cofins + totais.icms;
    const mediaMensais = totalMensais / numMeses;
    const totalTrimestrais = totais.csll + totais.irpj;
    const reservaMensal = totalTrimestrais / numMeses;

    return {
      mediaMensal: 0,
      impostosMensais: mediaMensais,
      reservaTrimestrais: reservaMensal,
      totalEsperado: mediaMensais + reservaMensal,
    };
  }, [faturamento.length, isSimples, totais]);

  // Dados para o gráfico de pizza
  const pieData = useMemo((): PieDataItem[] => {
    const dataItems: PieDataItem[] = [];
    if (totais.pis > 0) dataItems.push({ name: 'PIS', value: totais.pis });
    if (totais.cofins > 0) dataItems.push({ name: 'COFINS', value: totais.cofins });
    if (totais.icms > 0) dataItems.push({ name: 'ICMS', value: totais.icms });
    if (totais.sn > 0) dataItems.push({ name: 'Simples Nacional', value: totais.sn });
    if (totais.csll > 0) dataItems.push({ name: 'CSLL', value: totais.csll });
    if (totais.irpj > 0) dataItems.push({ name: 'IRPJ', value: totais.irpj });
    return dataItems;
  }, [totais]);

  // Dados para gráfico de evolução da alíquota (Simples Nacional)
  const aliquotaData = useMemo(() => {
    return faturamento.map(item => {
      const [year, month] = item.dt_faturamento.split('-');
      const aliquota =
        item.total_faturamento > 0 ? ((item.sn || 0) / item.total_faturamento) * 100 : 0;
      return {
        mes: `${month}/${year.slice(2)}`,
        faturamento: item.total_faturamento,
        simples: item.sn || 0,
        aliquota: parseFloat(aliquota.toFixed(2)),
      };
    });
  }, [faturamento]);

  // Dados para gráfico de barras (faturamento por mês)
  const barData = useMemo(() => {
    return faturamento.map(item => {
      const [year, month] = item.dt_faturamento.split('-');
      const impostosMes = (item.pis || 0) + (item.cofins || 0) + (item.icms || 0) + (item.sn || 0);
      return {
        mes: `${month}/${year.slice(2)}`,
        faturamento: item.total_faturamento,
        impostos: impostosMes,
        percentual:
          item.total_faturamento > 0
            ? ((impostosMes / item.total_faturamento) * 100).toFixed(1)
            : 0,
      };
    });
  }, [faturamento]);

  // Dados para gráfico de barras (evolução da folha)
  const folhaData = useMemo(() => {
    if (!folha || !folha.meses || folha.meses.length === 0) return [];
    return folha.meses.map(item => ({
      mes: item.mes_nome || mesesNomes[item.mes] || `Mês ${item.mes}`,
      mesNumero: item.mes,
      admissoes: item.admissoes || 0,
      demissoes: item.demissoes || 0,
      ferias: item.ferias || 0,
      afastamentos: item.afastamentos || 0,
      fgts: item.fgts || 0,
      dctfweb: item.dctfweb || 0,
    }));
  }, [folha]);

  // Custom tooltip para gráficos
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box bg="white" p={3} borderRadius="md" boxShadow="lg" border="1px" borderColor="gray.200">
          <Text fontWeight="bold" mb={2}>
            {label}
          </Text>
          {payload.map((entry: any) => (
            <Text key={entry.name} fontSize="sm" color={entry.color}>
              {entry.name}:{' '}
              {entry.name === 'percentual' ? `${entry.value}%` : formatCurrency(entry.value)}
            </Text>
          ))}
        </Box>
      );
    }
    return null;
  };

  const hasData = faturamento.length > 0 || (folha && folha.meses && folha.meses.length > 0);

  // Helper para cor do badge de percentual
  const getPercentBgColor = (percent: number) => {
    if (percent > 15) return 'red.100';
    if (percent > 10) return 'yellow.100';
    return 'green.100';
  };

  const getPercentTextColor = (percent: number) => {
    if (percent > 15) return 'red.700';
    if (percent > 10) return 'yellow.700';
    return 'green.700';
  };

  // Renderiza estado de loading
  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="400px">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color="gray.500">Carregando dados...</Text>
        </VStack>
      </Flex>
    );
  }

  // Renderiza estado vazio
  if (!hasData) {
    return (
      <Alert status="info" borderRadius="xl">
        <AlertIcon />
        Nenhum dado encontrado para o período selecionado.
      </Alert>
    );
  }

  return (
    <VStack id="dossie-dashboard-content" spacing={6} align="stretch">
      {/* ========================================== */}
      {/* CARDS GERAIS - Visão Consolidada */}
      {/* ========================================== */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 3, md: 5 }}>
        {/* Faturamento Total */}
        <Box
          bg="white"
          p={{ base: 4, md: 5 }}
          borderRadius="xl"
          boxShadow="sm"
          borderLeft="4px"
          borderColor="green.400"
        >
          <Flex align="center" mb={2}>
            <Flex
              align="center"
              justify="center"
              w={{ base: 8, md: 10 }}
              h={{ base: 8, md: 10 }}
              borderRadius="lg"
              bg="green.50"
            >
              <Icon as={CurrencyDollar} boxSize={{ base: 4, md: 5 }} color="green.500" />
            </Flex>
          </Flex>
          <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.500" fontWeight="medium">
            Faturamento Total
          </Text>
          <Text fontSize={{ base: 'lg', md: '2xl' }} fontWeight="bold" color="gray.800">
            {formatCurrencyCompact(totais.faturamento)}
          </Text>
          <Text fontSize="xs" color="gray.400">
            {faturamento.length} mês(es)
          </Text>
        </Box>

        {/* Total Geral de Impostos (Fiscal + DP) */}
        <Box
          bg="white"
          p={{ base: 4, md: 5 }}
          borderRadius="xl"
          boxShadow="sm"
          borderLeft="4px"
          borderColor="red.400"
        >
          <Flex align="center" mb={2}>
            <Flex
              align="center"
              justify="center"
              w={{ base: 8, md: 10 }}
              h={{ base: 8, md: 10 }}
              borderRadius="lg"
              bg="red.50"
            >
              <Icon as={Receipt} boxSize={{ base: 4, md: 5 }} color="red.500" />
            </Flex>
          </Flex>
          <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.500" fontWeight="medium">
            Total Impostos (Fiscal + DP)
          </Text>
          <Text fontSize={{ base: 'lg', md: '2xl' }} fontWeight="bold" color="gray.800">
            {formatCurrencyCompact(totalGeralImpostos)}
          </Text>
          <HStack fontSize="xs" spacing={2}>
            <Text color="orange.500">Fiscal: {formatCurrencyCompact(totalImpostosFiscal)}</Text>
            <Text color="purple.500">DP: {formatCurrencyCompact(totalImpostosDP)}</Text>
          </HStack>
        </Box>

        {/* Carga Tributária */}
        <Box
          bg="white"
          p={{ base: 4, md: 5 }}
          borderRadius="xl"
          boxShadow="sm"
          borderLeft="4px"
          borderColor="purple.400"
        >
          <Flex align="center" mb={2}>
            <Flex
              align="center"
              justify="center"
              w={{ base: 8, md: 10 }}
              h={{ base: 8, md: 10 }}
              borderRadius="lg"
              bg="purple.50"
            >
              <Icon as={Percent} boxSize={{ base: 4, md: 5 }} color="purple.500" />
            </Flex>
          </Flex>
          <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.500" fontWeight="medium">
            Carga Tributária
          </Text>
          <Text fontSize={{ base: 'lg', md: '2xl' }} fontWeight="bold" color="gray.800">
            {percentualImpostos.toFixed(1)}%
          </Text>
          <Text fontSize="xs" color="gray.400">
            Impostos fiscais e DP / Faturamento
          </Text>
        </Box>

        {/* Colaboradores */}
        <Box
          bg="white"
          p={{ base: 4, md: 5 }}
          borderRadius="xl"
          boxShadow="sm"
          borderLeft="4px"
          borderColor="blue.400"
        >
          <Flex align="center" mb={2}>
            <Flex
              align="center"
              justify="center"
              w={{ base: 8, md: 10 }}
              h={{ base: 8, md: 10 }}
              borderRadius="lg"
              bg="blue.50"
            >
              <Icon as={Users} boxSize={{ base: 4, md: 5 }} color="blue.500" />
            </Flex>
          </Flex>
          <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.500" fontWeight="medium">
            Colaboradores
          </Text>
          <Text fontSize={{ base: 'lg', md: '2xl' }} fontWeight="bold" color="gray.800">
            {totaisFolha.vidas}
          </Text>
          <HStack spacing={2} fontSize="xs">
            <Text color="green.500">+{totaisFolha.admissoes}</Text>
            <Text color="red.500">-{totaisFolha.demissoes}</Text>
          </HStack>
        </Box>
      </SimpleGrid>

      {/* ========================================== */}
      {/* SEÇÃO FISCAL */}
      {/* ========================================== */}
      <Box bg="white" borderRadius="xl" boxShadow="sm" overflow="hidden">
        {/* Header da seção Fiscal */}
        <Box bg="orange.500" px={5} py={3}>
          <Flex align="center" gap={2}>
            <Icon as={Buildings} boxSize={5} color="white" />
            <Text fontSize="lg" fontWeight="bold" color="white">
              Fiscal
            </Text>
          </Flex>
        </Box>

        <Box p={{ base: 4, md: 6 }}>
          {/* KPI Cards Fiscais */}
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
            <Box p={4} bg="orange.50" borderRadius="lg" textAlign="center">
              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                Total Impostos Fiscais
              </Text>
              <Text fontSize="xl" fontWeight="bold" color="orange.600">
                {formatCurrencyCompact(totalImpostosFiscal)}
              </Text>
            </Box>
            <Box p={4} bg="green.50" borderRadius="lg" textAlign="center">
              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                Faturamento
              </Text>
              <Text fontSize="xl" fontWeight="bold" color="green.600">
                {formatCurrencyCompact(totais.faturamento)}
              </Text>
            </Box>
            <Box p={4} bg="purple.50" borderRadius="lg" textAlign="center">
              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                Carga Tributária
              </Text>
              <Text fontSize="xl" fontWeight="bold" color="purple.600">
                {percentualImpostos.toFixed(2)}%
              </Text>
            </Box>
            <Box p={4} bg="teal.50" borderRadius="lg" textAlign="center">
              <Flex align="center" justify="center" gap={1} mb={1}>
                <Icon as={Calculator} boxSize={3} color="teal.500" />
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  Média Mensal de Impostos
                </Text>
              </Flex>
              <Text fontSize="xl" fontWeight="bold" color="teal.600">
                {formatCurrencyCompact(
                  isSimples ? projecaoMensal.mediaMensal : projecaoMensal.totalEsperado,
                )}
              </Text>
              <Text fontSize="2xs" color="gray.400">
                {isSimples ? 'DAS a pagar por mês' : 'Provisão mensal esperada'}
              </Text>
            </Box>
          </SimpleGrid>

          {/* Gráficos Fiscais */}
          <VStack spacing={6} mb={6} align="stretch">
            {/* Gráfico condicional: Simples Nacional = Evolução da Alíquota / Outros = Pizza */}
            <Box>
              <Box bg="gray.50" p={4} borderRadius="lg">
                {isSimples ? (
                  <>
                    <Flex align="center" mb={4}>
                      <Icon as={TrendUp} boxSize={5} color="purple.500" mr={2} />
                      <Text fontSize="md" fontWeight="bold" color="gray.700">
                        Evolução da Alíquota Efetiva
                      </Text>
                    </Flex>

                    {aliquotaData.length === 0 ? (
                      <Flex justify="center" align="center" h="220px">
                        <Text color="gray.400">Sem dados de impostos</Text>
                      </Flex>
                    ) : (
                      <VStack spacing={4} align="stretch">
                        <Box h="200px">
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart
                              data={aliquotaData}
                              margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                            >
                              <defs>
                                <linearGradient id="colorAliquota" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#805AD5" stopOpacity={0.8} />
                                  <stop offset="95%" stopColor="#805AD5" stopOpacity={0.1} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                              <XAxis
                                dataKey="mes"
                                tick={{ fontSize: 11, fill: '#718096' }}
                                axisLine={{ stroke: '#E2E8F0' }}
                              />
                              <YAxis
                                tick={{ fontSize: 11, fill: '#718096' }}
                                axisLine={{ stroke: '#E2E8F0' }}
                                tickFormatter={value => `${value}%`}
                                domain={[0, 'auto']}
                              />
                              <Tooltip
                                formatter={(value: number, name: string) => {
                                  if (name === 'aliquota') return [`${value}%`, 'Alíquota Efetiva'];
                                  return [
                                    formatCurrency(value),
                                    name === 'simples' ? 'Simples Nacional' : name,
                                  ];
                                }}
                              />
                              <Area
                                type="monotone"
                                dataKey="aliquota"
                                name="aliquota"
                                stroke="#805AD5"
                                fill="url(#colorAliquota)"
                                strokeWidth={2}
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </Box>
                        <SimpleGrid columns={3} spacing={2}>
                          <Box bg="white" p={2} borderRadius="md" textAlign="center">
                            <Text fontSize="xs" color="gray.500">
                              Média
                            </Text>
                            <Text fontSize="md" fontWeight="bold" color="purple.600">
                              {percentualImpostos.toFixed(2)}%
                            </Text>
                          </Box>
                          <Box bg="white" p={2} borderRadius="md" textAlign="center">
                            <Text fontSize="xs" color="gray.500">
                              Menor
                            </Text>
                            <Text fontSize="md" fontWeight="bold" color="green.600">
                              {aliquotaData.length > 0
                                ? Math.min(...aliquotaData.map(d => d.aliquota)).toFixed(2)
                                : '0.00'}
                              %
                            </Text>
                          </Box>
                          <Box bg="white" p={2} borderRadius="md" textAlign="center">
                            <Text fontSize="xs" color="gray.500">
                              Maior
                            </Text>
                            <Text fontSize="md" fontWeight="bold" color="orange.600">
                              {aliquotaData.length > 0
                                ? Math.max(...aliquotaData.map(d => d.aliquota)).toFixed(2)
                                : '0.00'}
                              %
                            </Text>
                          </Box>
                        </SimpleGrid>
                      </VStack>
                    )}
                  </>
                ) : (
                  <>
                    <Flex align="center" mb={4}>
                      <Icon as={ChartPie} boxSize={5} color="purple.500" mr={2} />
                      <Text fontSize="md" fontWeight="bold" color="gray.700">
                        Distribuição de Impostos
                      </Text>
                    </Flex>

                    {pieData.length === 0 ? (
                      <Flex justify="center" align="center" h="220px">
                        <Text color="gray.400">Sem dados de impostos</Text>
                      </Flex>
                    ) : (
                      <Flex direction={{ base: 'column', md: 'row' }} align="center">
                        <Box w={{ base: '100%', md: '55%' }} h="220px">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                innerRadius={50}
                                outerRadius={80}
                                paddingAngle={2}
                                dataKey="value"
                              >
                                {pieData.map((entry, index) => (
                                  <Cell
                                    key={entry.name}
                                    fill={PIE_COLORS[index % PIE_COLORS.length]}
                                  />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value: number) => formatCurrency(value)} />
                            </PieChart>
                          </ResponsiveContainer>
                        </Box>
                        <VStack align="stretch" spacing={1} flex={1} mt={{ base: 2, md: 0 }}>
                          {pieData.map((item, index) => {
                            const percent =
                              totalImpostosFiscal > 0
                                ? ((item.value / totalImpostosFiscal) * 100).toFixed(1)
                                : '0';
                            return (
                              <Flex key={item.name} align="center" justify="space-between">
                                <HStack spacing={1}>
                                  <Box
                                    w={2}
                                    h={2}
                                    borderRadius="sm"
                                    bg={PIE_COLORS[index % PIE_COLORS.length]}
                                  />
                                  <Text fontSize="xs" color="gray.600">
                                    {item.name}
                                  </Text>
                                </HStack>
                                <Text fontSize="xs" fontWeight="bold" color="gray.700">
                                  {formatCurrencyCompact(item.value)} ({percent}%)
                                </Text>
                              </Flex>
                            );
                          })}
                        </VStack>
                      </Flex>
                    )}
                  </>
                )}
              </Box>
            </Box>

            {/* Gráfico de Barras - Faturamento vs Impostos */}
            <Box>
              <Box bg="gray.50" p={4} borderRadius="lg">
                <Flex align="center" mb={4}>
                  <Icon as={TrendUp} boxSize={5} color="green.500" mr={2} />
                  <Text fontSize="md" fontWeight="bold" color="gray.700">
                    Faturamento x Impostos
                  </Text>
                </Flex>

                {barData.length === 0 ? (
                  <Flex justify="center" align="center" h="250px">
                    <Text color="gray.400">Sem dados de faturamento</Text>
                  </Flex>
                ) : (
                  <Box h="300px">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={barData}
                        margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis
                          dataKey="mes"
                          tick={{ fontSize: 11, fill: '#718096' }}
                          axisLine={{ stroke: '#E2E8F0' }}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: '#718096' }}
                          axisLine={{ stroke: '#E2E8F0' }}
                          tickFormatter={value => formatCurrencyCompact(value)}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '11px' }} />
                        <Bar
                          dataKey="faturamento"
                          name="Faturamento"
                          fill="#48BB78"
                          radius={[4, 4, 0, 0]}
                        />
                        <Bar
                          dataKey="impostos"
                          name="Impostos"
                          fill="#ED8936"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Box>
            </Box>
          </VStack>

          {/* Detalhamento de Impostos - só exibe para não-Simples com dados */}
          {!isSimples && hasImpostosData && (
            <Box mb={6}>
              <Text fontSize="md" fontWeight="bold" color="gray.700" mb={3}>
                Detalhamento de Impostos
              </Text>
              <SimpleGrid columns={{ base: 2, sm: 3, md: totais.sn > 0 ? 6 : 5 }} spacing={3}>
                {[
                  { label: 'PIS', value: totais.pis, color: 'blue' },
                  { label: 'COFINS', value: totais.cofins, color: 'green' },
                  { label: 'ICMS', value: totais.icms, color: 'orange' },
                  { label: 'CSLL', value: totais.csll, color: 'pink' },
                  { label: 'IRPJ', value: totais.irpj, color: 'red' },
                  ...(totais.sn > 0 ? [{ label: 'SN', value: totais.sn, color: 'purple' }] : []),
                ].map(item => (
                  <Box
                    key={item.label}
                    p={3}
                    bg={`${item.color}.50`}
                    borderRadius="lg"
                    textAlign="center"
                  >
                    <Text fontSize="xs" color="gray.500" fontWeight="medium">
                      {item.label}
                    </Text>
                    <Text fontSize="sm" fontWeight="bold" color={`${item.color}.600`}>
                      {formatCurrencyCompact(item.value)}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          )}

          {/* Projeção Mensal - só exibe para não-Simples com dados */}
          {!isSimples && hasImpostosData && (
            <Box>
              <Flex align="center" mb={3}>
                <Icon as={Calculator} boxSize={4} color="teal.500" mr={2} />
                <Text fontSize="md" fontWeight="bold" color="gray.700">
                  Projeção Mensal
                </Text>
              </Flex>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Box p={4} bg="blue.50" borderRadius="lg" borderLeft="4px" borderColor="blue.400">
                  <Flex align="center" gap={2} mb={1}>
                    <Icon as={Wallet} boxSize={4} color="blue.500" />
                    <Text fontSize="sm" color="gray.500" fontWeight="medium">
                      Impostos Mensais
                    </Text>
                  </Flex>
                  <Text fontSize="xl" fontWeight="bold" color="blue.600">
                    {formatCurrency(projecaoMensal.impostosMensais)}
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    PIS + COFINS + ICMS
                  </Text>
                </Box>
                <Box
                  p={4}
                  bg="orange.50"
                  borderRadius="lg"
                  borderLeft="4px"
                  borderColor="orange.400"
                >
                  <Flex align="center" gap={2} mb={1}>
                    <Icon as={Vault} boxSize={4} color="orange.500" />
                    <Text fontSize="sm" color="gray.500" fontWeight="medium">
                      Reserva Trimestrais
                    </Text>
                  </Flex>
                  <Text fontSize="xl" fontWeight="bold" color="orange.600">
                    {formatCurrency(projecaoMensal.reservaTrimestrais)}
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    CSLL + IRPJ
                  </Text>
                </Box>
                <Box p={4} bg="teal.50" borderRadius="lg" borderLeft="4px" borderColor="teal.400">
                  <Flex align="center" gap={2} mb={1}>
                    <Icon as={Calculator} boxSize={4} color="teal.500" />
                    <Text fontSize="sm" color="gray.500" fontWeight="medium">
                      Total Esperado
                    </Text>
                  </Flex>
                  <Text fontSize="xl" fontWeight="bold" color="teal.600">
                    {formatCurrency(projecaoMensal.totalEsperado)}
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    Provisão mensal
                  </Text>
                </Box>
              </SimpleGrid>
            </Box>
          )}

          {/* Tabela de Faturamento Mensal */}
          {faturamento.length > 0 && (
            <Box mt={6}>
              <Text fontSize="md" fontWeight="bold" color="gray.700" mb={3}>
                Detalhamento Mensal
              </Text>
              <Box overflowX="auto" bg="gray.50" borderRadius="lg">
                <Box as="table" w="100%" fontSize="sm">
                  <Box as="thead" bg="gray.100">
                    <Box as="tr">
                      <Box as="th" p={3} textAlign="left" color="gray.600" fontWeight="semibold">
                        Mês
                      </Box>
                      <Box as="th" p={3} textAlign="right" color="gray.600" fontWeight="semibold">
                        Faturamento
                      </Box>
                      <Box
                        as="th"
                        p={3}
                        textAlign="right"
                        color="gray.600"
                        fontWeight="semibold"
                        display={{ base: 'none', md: 'table-cell' }}
                      >
                        Serviços
                      </Box>
                      <Box as="th" p={3} textAlign="right" color="gray.600" fontWeight="semibold">
                        Impostos
                      </Box>
                      <Box as="th" p={3} textAlign="right" color="gray.600" fontWeight="semibold">
                        %
                      </Box>
                    </Box>
                  </Box>
                  <Box as="tbody">
                    {faturamento.map((item, index) => {
                      const [year, month] = item.dt_faturamento.split('-');
                      const impostosMes =
                        (item.pis || 0) +
                        (item.cofins || 0) +
                        (item.icms || 0) +
                        (item.sn || 0) +
                        (item.csll || 0);
                      const percentMes =
                        item.total_faturamento > 0
                          ? (impostosMes / item.total_faturamento) * 100
                          : 0;
                      return (
                        <Box
                          as="tr"
                          key={item.dt_faturamento}
                          bg={index % 2 === 0 ? 'white' : 'gray.50'}
                          _hover={{ bg: 'orange.50' }}
                        >
                          <Box as="td" p={3} fontWeight="medium">
                            {month}/{year}
                          </Box>
                          <Box
                            as="td"
                            p={3}
                            textAlign="right"
                            color="green.600"
                            fontWeight="semibold"
                          >
                            {formatCurrency(item.total_faturamento)}
                          </Box>
                          <Box
                            as="td"
                            p={3}
                            textAlign="right"
                            display={{ base: 'none', md: 'table-cell' }}
                          >
                            {formatCurrency(item.total_servicos)}
                          </Box>
                          <Box as="td" p={3} textAlign="right" color="orange.600">
                            {formatCurrency(impostosMes)}
                          </Box>
                          <Box as="td" p={3} textAlign="right">
                            <Box
                              as="span"
                              px={2}
                              py={1}
                              borderRadius="full"
                              fontSize="xs"
                              fontWeight="bold"
                              bg={getPercentBgColor(percentMes)}
                              color={getPercentTextColor(percentMes)}
                            >
                              {percentMes.toFixed(1)}%
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* ========================================== */}
      {/* SEÇÃO DEPARTAMENTO PESSOAL */}
      {/* ========================================== */}
      <Box bg="white" borderRadius="xl" boxShadow="sm" overflow="hidden" position="relative">
        {/* Overlay de loading */}
        {isLoadingFolha && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="whiteAlpha.800"
            borderRadius="xl"
            zIndex={10}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <VStack spacing={3}>
              <Spinner size="lg" color="blue.500" thickness="3px" />
              <Text color="gray.600" fontSize="sm">
                Carregando folha de pagamento...
              </Text>
            </VStack>
          </Box>
        )}

        {/* Header da seção DP */}
        <Box bg="blue.500" px={5} py={3}>
          <Flex align="center" justify="space-between" flexWrap="wrap" gap={2}>
            <Flex align="center" gap={2}>
              <Icon as={Users} boxSize={5} color="white" />
              <Text fontSize="lg" fontWeight="bold" color="white">
                Departamento Pessoal
              </Text>
            </Flex>
            {/* Seletor de ano */}
            {anoFolha && onAnoFolhaChange && (
              <Flex align="center" gap={2}>
                <Text fontSize="sm" color="white" fontWeight="medium">
                  Ano:
                </Text>
                <Select
                  size="sm"
                  w="100px"
                  value={anoFolha}
                  onChange={e => onAnoFolhaChange(Number(e.target.value))}
                  bg="white"
                  borderColor="gray.200"
                  isDisabled={isLoadingFolha}
                >
                  {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 5 + i).map(
                    ano => (
                      <option key={ano} value={ano}>
                        {ano}
                      </option>
                    ),
                  )}
                </Select>
              </Flex>
            )}
          </Flex>
        </Box>

        <Box p={{ base: 4, md: 6 }}>
          {/* KPI Cards DP */}
          <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} spacing={4} mb={6}>
            <Box p={4} bg="purple.50" borderRadius="lg" textAlign="center">
              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                FGTS Total
              </Text>
              <Text fontSize="xl" fontWeight="bold" color="purple.600">
                {formatCurrencyCompact(totaisFolha.fgts)}
              </Text>
            </Box>
            <Box p={4} bg="blue.50" borderRadius="lg" textAlign="center">
              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                DCTF-Web Total
              </Text>
              <Text fontSize="xl" fontWeight="bold" color="blue.600">
                {formatCurrencyCompact(totaisFolha.dctfweb)}
              </Text>
            </Box>
            <Box p={4} bg="teal.50" borderRadius="lg" textAlign="center">
              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                Colaboradores
              </Text>
              <Text fontSize="xl" fontWeight="bold" color="teal.600">
                {totaisFolha.vidas}
              </Text>
              {totaisFolha.prolabore > 0 && (
                <Text fontSize="xs" color="gray.400">
                  + {totaisFolha.prolabore} pró-labore
                </Text>
              )}
            </Box>
            <Box p={4} bg="yellow.50" borderRadius="lg" textAlign="center">
              <Flex align="center" justify="center" gap={1} mb={1}>
                <Icon as={Sun} boxSize={3} color="yellow.600" />
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  Férias
                </Text>
              </Flex>
              <Text fontSize="xl" fontWeight="bold" color="yellow.600">
                {totaisFolha.ferias}
              </Text>
            </Box>
            <Box p={4} bg="red.50" borderRadius="lg" textAlign="center">
              <Flex align="center" justify="center" gap={1} mb={1}>
                <Icon as={FirstAidKit} boxSize={3} color="red.500" />
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  Afastamentos
                </Text>
              </Flex>
              <Text fontSize="xl" fontWeight="bold" color="red.500">
                {totaisFolha.afastamentos}
              </Text>
            </Box>
            <Box p={4} bg="gray.50" borderRadius="lg" textAlign="center">
              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                Movimentação
              </Text>
              <HStack justify="center" spacing={3}>
                <Flex align="center" gap={1}>
                  <Icon as={UserPlus} boxSize={3} color="green.600" />
                  <Text fontSize="lg" fontWeight="bold" color="green.600">
                    {totaisFolha.admissoes}
                  </Text>
                </Flex>
                <Flex align="center" gap={1}>
                  <Icon as={UserMinus} boxSize={3} color="red.600" />
                  <Text fontSize="lg" fontWeight="bold" color="red.600">
                    {totaisFolha.demissoes}
                  </Text>
                </Flex>
              </HStack>
            </Box>
          </SimpleGrid>

          {/* Gráfico de barras - FGTS e DCTF-Web por mês */}
          {folhaData.length > 0 && (
            <Box bg="gray.50" p={4} borderRadius="lg" mb={6}>
              <Flex align="center" mb={4}>
                <Icon as={TrendUp} boxSize={5} color="blue.500" mr={2} />
                <Text fontSize="md" fontWeight="bold" color="gray.700">
                  Evolução FGTS e DCTF-Web
                </Text>
              </Flex>
              <Box h="260px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={folhaData} margin={{ top: 25, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis
                      dataKey="mes"
                      tick={{ fontSize: 11, fill: '#718096' }}
                      axisLine={{ stroke: '#E2E8F0' }}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#718096' }}
                      axisLine={{ stroke: '#E2E8F0' }}
                      tickFormatter={value => formatCurrencyCompact(value)}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => [formatCurrency(value), name]}
                    />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Bar dataKey="fgts" name="FGTS" fill="#805AD5" radius={[4, 4, 0, 0]}>
                      <LabelList
                        dataKey="fgts"
                        position="top"
                        formatter={(value: number) => formatCurrencyCompact(value)}
                        style={{ fontSize: 9, fill: '#553C9A', fontWeight: 600 }}
                      />
                    </Bar>
                    <Bar dataKey="dctfweb" name="DCTF-Web" fill="#3182CE" radius={[4, 4, 0, 0]}>
                      <LabelList
                        dataKey="dctfweb"
                        position="top"
                        formatter={(value: number) => formatCurrencyCompact(value)}
                        style={{ fontSize: 9, fill: '#2C5282', fontWeight: 600 }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          )}

          {/* Tabela detalhada da folha */}
          {folhaData.length > 0 && (
            <Box>
              <Text fontSize="md" fontWeight="bold" color="gray.700" mb={3}>
                Detalhamento Mensal
              </Text>
              <Box overflowX="auto" bg="gray.50" borderRadius="lg">
                <Box as="table" w="100%" fontSize="sm">
                  <Box as="thead" bg="gray.100">
                    <Box as="tr">
                      <Box as="th" p={3} textAlign="left" color="gray.600" fontWeight="semibold">
                        Mês
                      </Box>
                      <Box as="th" p={3} textAlign="right" color="gray.600" fontWeight="semibold">
                        FGTS
                      </Box>
                      <Box as="th" p={3} textAlign="right" color="gray.600" fontWeight="semibold">
                        DCTF-Web
                      </Box>
                      <Box
                        as="th"
                        p={3}
                        textAlign="center"
                        color="gray.600"
                        fontWeight="semibold"
                        display={{ base: 'none', md: 'table-cell' }}
                      >
                        <Flex align="center" justify="center" gap={1}>
                          <Icon as={Sun} boxSize={3} />
                          Férias
                        </Flex>
                      </Box>
                      <Box
                        as="th"
                        p={3}
                        textAlign="center"
                        color="gray.600"
                        fontWeight="semibold"
                        display={{ base: 'none', md: 'table-cell' }}
                      >
                        <Flex align="center" justify="center" gap={1}>
                          <Icon as={FirstAidKit} boxSize={3} />
                          Afast.
                        </Flex>
                      </Box>
                      <Box as="th" p={3} textAlign="center" color="gray.600" fontWeight="semibold">
                        <Flex align="center" justify="center" gap={1}>
                          <Icon as={UserPlus} boxSize={3} />
                          Adm.
                        </Flex>
                      </Box>
                      <Box as="th" p={3} textAlign="center" color="gray.600" fontWeight="semibold">
                        <Flex align="center" justify="center" gap={1}>
                          <Icon as={UserMinus} boxSize={3} />
                          Dem.
                        </Flex>
                      </Box>
                    </Box>
                  </Box>
                  <Box as="tbody">
                    {folhaData.map((item, index) => (
                      <Box
                        as="tr"
                        key={item.mesNumero}
                        bg={index % 2 === 0 ? 'white' : 'gray.50'}
                        _hover={{ bg: 'blue.50' }}
                      >
                        <Box as="td" p={3} fontWeight="medium">
                          {item.mes}
                        </Box>
                        <Box
                          as="td"
                          p={3}
                          textAlign="right"
                          color="purple.600"
                          fontWeight="semibold"
                        >
                          {formatCurrency(item.fgts)}
                        </Box>
                        <Box as="td" p={3} textAlign="right" color="blue.600">
                          {formatCurrency(item.dctfweb)}
                        </Box>
                        <Box
                          as="td"
                          p={3}
                          textAlign="center"
                          display={{ base: 'none', md: 'table-cell' }}
                        >
                          {item.ferias > 0 ? (
                            <Text as="span" color="yellow.600" fontWeight="medium">
                              {item.ferias}
                            </Text>
                          ) : (
                            <Text as="span" color="gray.400">
                              0
                            </Text>
                          )}
                        </Box>
                        <Box
                          as="td"
                          p={3}
                          textAlign="center"
                          display={{ base: 'none', md: 'table-cell' }}
                        >
                          {item.afastamentos > 0 ? (
                            <Text as="span" color="red.500" fontWeight="medium">
                              {item.afastamentos}
                            </Text>
                          ) : (
                            <Text as="span" color="gray.400">
                              0
                            </Text>
                          )}
                        </Box>
                        <Box as="td" p={3} textAlign="center">
                          {item.admissoes > 0 ? (
                            <Text as="span" color="green.500" fontWeight="medium">
                              +{item.admissoes}
                            </Text>
                          ) : (
                            <Text as="span" color="gray.400">
                              0
                            </Text>
                          )}
                        </Box>
                        <Box as="td" p={3} textAlign="center">
                          {item.demissoes > 0 ? (
                            <Text as="span" color="red.500" fontWeight="medium">
                              -{item.demissoes}
                            </Text>
                          ) : (
                            <Text as="span" color="gray.400">
                              0
                            </Text>
                          )}
                        </Box>
                      </Box>
                    ))}
                    {/* Linha de totais */}
                    <Box as="tr" bg="blue.100" fontWeight="bold">
                      <Box as="td" p={3}>
                        Total
                      </Box>
                      <Box as="td" p={3} textAlign="right" color="purple.700">
                        {formatCurrency(totaisFolha.fgts)}
                      </Box>
                      <Box as="td" p={3} textAlign="right" color="blue.700">
                        {formatCurrency(totaisFolha.dctfweb)}
                      </Box>
                      <Box
                        as="td"
                        p={3}
                        textAlign="center"
                        color="yellow.700"
                        display={{ base: 'none', md: 'table-cell' }}
                      >
                        {totaisFolha.ferias}
                      </Box>
                      <Box
                        as="td"
                        p={3}
                        textAlign="center"
                        color="red.600"
                        display={{ base: 'none', md: 'table-cell' }}
                      >
                        {totaisFolha.afastamentos}
                      </Box>
                      <Box as="td" p={3} textAlign="center" color="green.600">
                        +{totaisFolha.admissoes}
                      </Box>
                      <Box as="td" p={3} textAlign="center" color="red.600">
                        -{totaisFolha.demissoes}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* Mensagem quando não há dados de folha */}
          {(!folha || folhaData.length === 0) && !isLoadingFolha && (
            <Flex justify="center" align="center" py={8} bg="gray.50" borderRadius="lg">
              <Text color="gray.500" fontSize="sm">
                Nenhum dado de folha encontrado para o período
              </Text>
            </Flex>
          )}
        </Box>
      </Box>
    </VStack>
  );
}
